import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Truck, Package, Upload, Loader2, Plus, X, Paperclip, Camera } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const MODES_PAIEMENT = [
    { value: 'cheque_o', label: 'Chèque à l\'ordre' },
    { value: 'cheque_so', label: 'Chèque sans ordre' },
    { value: 'espece', label: 'Espèces' },
    { value: 'virement', label: 'Virement' },
];

const COMMERCIAUX = ['Axel', 'Melissa', 'Syphax', 'Isaac'];

// Compresse une photo côté téléphone avant l'envoi : redimensionne à 1600px max
// et convertit en JPEG qualité 72%. Une photo mobile de 3-8 MB tombe à ~150-400 KB
// -> upload rapide sur le terrain (4G) et base de données allégée.
const IMG_MAX_DIM = 1600;
const IMG_JPEG_QUALITY = 0.72;

const compressImage = (file) => new Promise((resolve) => {
    // Fichiers non-image ou déjà légers : inchangés
    if (!file.type || !file.type.startsWith('image/') || file.size <= 300 * 1024) {
        resolve(file);
        return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        URL.revokeObjectURL(url);
        try {
            let { width, height } = img;
            if (width > IMG_MAX_DIM || height > IMG_MAX_DIM) {
                const ratio = Math.min(IMG_MAX_DIM / width, IMG_MAX_DIM / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
                if (blob && blob.size < file.size) {
                    const base = file.name.includes('.') ? file.name.slice(0, file.name.lastIndexOf('.')) : file.name;
                    resolve(new File([blob], `${base}.jpg`, { type: 'image/jpeg' }));
                } else {
                    resolve(file);
                }
            }, 'image/jpeg', IMG_JPEG_QUALITY);
        } catch {
            resolve(file);
        }
    };
    img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
    };
    img.src = url;
});

export default function EspaceDemenageursFormPage() {
    const [typePrestation, setTypePrestation] = useState('demenagement');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Champs communs
    const [nomClient, setNomClient] = useState('');
    const [montantEncaisse, setMontantEncaisse] = useState('');
    const [modePaiement, setModePaiement] = useState('');
    const [clientNonRegle, setClientNonRegle] = useState(false);
    const [preuveFiles, setPreuveFiles] = useState([]); // File[]
    const [signalement, setSignalement] = useState('');

    // Déménagement
    const [heureFin, setHeureFin] = useState('');
    const [volumeDevis, setVolumeDevis] = useState('');
    const [volumeReel, setVolumeReel] = useState('');
    const [commercial, setCommercial] = useState('');
    const [chefEquipe, setChefEquipe] = useState('');

    // Livraison
    const [livreur, setLivreur] = useState('');

    const resetForm = () => {
        setNomClient('');
        setMontantEncaisse('');
        setModePaiement('');
        setClientNonRegle(false);
        setPreuveFiles([]);
        setSignalement('');
        setHeureFin('');
        setVolumeDevis('');
        setVolumeReel('');
        setCommercial('');
        setChefEquipe('');
        setLivreur('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation: tous les champs sont obligatoires sauf "signalement"
        if (!nomClient.trim()) { toast.error('Veuillez saisir le nom du client'); return; }
        if (!clientNonRegle) {
            if (!montantEncaisse.toString().trim()) { toast.error('Veuillez saisir le montant encaissé'); return; }
            if (!modePaiement) { toast.error('Veuillez sélectionner le mode de paiement'); return; }
            if (modePaiement !== 'espece' && preuveFiles.length === 0) { toast.error('Veuillez ajouter au moins une preuve de paiement'); return; }
        }

        if (typePrestation === 'demenagement') {
            if (!heureFin) { toast.error('Veuillez saisir l\'heure de fin de chantier'); return; }
            if (!volumeDevis.toString().trim()) { toast.error('Veuillez saisir le volume devis'); return; }
            if (!volumeReel.toString().trim()) { toast.error('Veuillez saisir le volume réel'); return; }
            if (!commercial) { toast.error('Veuillez choisir un commercial'); return; }
            if (!chefEquipe.trim()) { toast.error('Veuillez saisir le chef d\'équipe'); return; }
        } else if (typePrestation === 'livraison') {
            if (!livreur.trim()) { toast.error('Veuillez saisir le livreur'); return; }
        }

        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('type_prestation', typePrestation);
            fd.append('nom_client', nomClient);
            fd.append('montant_encaisse', clientNonRegle ? '0' : montantEncaisse);
            fd.append('mode_paiement', clientNonRegle ? '' : modePaiement);
            fd.append('client_non_regle', clientNonRegle ? 'true' : 'false');
            fd.append('signalement_probleme', signalement || '');
            if (!clientNonRegle && modePaiement !== 'espece') {
                preuveFiles.forEach(f => fd.append('preuve_paiements', f));
            }
            if (typePrestation === 'demenagement') {
                fd.append('heure_fin_chantier', heureFin);
                fd.append('volume_devis', volumeDevis);
                fd.append('volume_reel', volumeReel);
                fd.append('commercial', commercial);
                fd.append('chef_equipe', chefEquipe);
            } else {
                fd.append('livreur', livreur);
            }
            await axios.post(`${API}/api/exploitation/espace-demenageurs`, fd);
            setSubmitted(true);
            resetForm();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'envoi du formulaire");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <Card className="max-w-md w-full border-emerald-200 bg-white shadow-md">
                    <CardContent className="p-8 text-center">
                        <CheckCircle2 size={56} className="text-emerald-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Merci !
                        </h2>
                        <p className="text-slate-600 text-sm">
                            Votre saisie a été enregistrée avec succès.
                        </p>
                        <Button
                            className="mt-6 bg-[#2e7cd6] hover:bg-[#2568b5] text-white"
                            onClick={() => setSubmitted(false)}
                            data-testid="new-entry-btn"
                        >
                            Nouvelle saisie
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 py-6 px-4">
            <div className="max-w-xl mx-auto">
                <div className="mb-6 text-center">
                    <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Espace déménageurs
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Remplissez les informations de votre prestation.
                    </p>
                </div>

                {/* Switch type */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                        type="button"
                        onClick={() => setTypePrestation('demenagement')}
                        className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${typePrestation === 'demenagement' ? 'border-[#2e7cd6] bg-blue-50' : 'border-slate-200 bg-white'}`}
                        data-testid="type-demenagement-btn"
                    >
                        <Truck size={22} className={typePrestation === 'demenagement' ? 'text-[#2e7cd6]' : 'text-slate-400'} />
                        <span className={`text-sm font-medium ${typePrestation === 'demenagement' ? 'text-[#2e7cd6]' : 'text-slate-500'}`}>Déménagement</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setTypePrestation('livraison')}
                        className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${typePrestation === 'livraison' ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white'}`}
                        data-testid="type-livraison-btn"
                    >
                        <Package size={22} className={typePrestation === 'livraison' ? 'text-amber-500' : 'text-slate-400'} />
                        <span className={`text-sm font-medium ${typePrestation === 'livraison' ? 'text-amber-600' : 'text-slate-500'}`}>Livraison</span>
                    </button>
                </div>

                <Card className="bg-white border border-slate-200 shadow-sm">
                    <CardContent className="p-5">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label className="text-sm text-slate-700 mb-1 block">Nom du client *</Label>
                                <Input value={nomClient} onChange={(e) => setNomClient(e.target.value)} data-testid="nom-client" required />
                            </div>

                            {typePrestation === 'demenagement' && (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-sm text-slate-700 mb-1 block">Heure fin chantier *</Label>
                                            <Input type="time" value={heureFin} onChange={(e) => setHeureFin(e.target.value)} data-testid="heure-fin" required />
                                        </div>
                                        <div>
                                            <Label className="text-sm text-slate-700 mb-1 block">Volume devis (m³) *</Label>
                                            <Input type="number" step="0.1" value={volumeDevis} onChange={(e) => setVolumeDevis(e.target.value)} data-testid="volume-devis" required />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-sm text-slate-700 mb-1 block">Volume réel (m³) *</Label>
                                        <Input type="number" step="0.1" value={volumeReel} onChange={(e) => setVolumeReel(e.target.value)} data-testid="volume-reel" required />
                                    </div>
                                    <div>
                                        <Label className="text-sm text-slate-700 mb-1 block">Commercial *</Label>
                                        <Select value={commercial} onValueChange={setCommercial} required>
                                            <SelectTrigger data-testid="commercial-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                                            <SelectContent>
                                                {COMMERCIAUX.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-sm text-slate-700 mb-1 block">Chef d'équipe *</Label>
                                        <Input value={chefEquipe} onChange={(e) => setChefEquipe(e.target.value)} data-testid="chef-equipe" required />
                                    </div>
                                </>
                            )}

                            {typePrestation === 'livraison' && (
                                <div>
                                    <Label className="text-sm text-slate-700 mb-1 block">Livreur *</Label>
                                    <Input value={livreur} onChange={(e) => setLivreur(e.target.value)} data-testid="livreur" required />
                                </div>
                            )}

                            {/* Montant + mode + preuve */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-sm text-slate-700 mb-1 block">Montant encaissé (€) {clientNonRegle ? '' : '*'}</Label>
                                    <Input type="number" step="0.01" value={clientNonRegle ? '0' : montantEncaisse} onChange={(e) => setMontantEncaisse(e.target.value)} data-testid="montant" disabled={clientNonRegle} required={!clientNonRegle} />
                                </div>
                                {!clientNonRegle && (
                                    <div>
                                        <Label className="text-sm text-slate-700 mb-1 block">Mode de paiement *</Label>
                                        <Select value={modePaiement} onValueChange={setModePaiement}>
                                            <SelectTrigger data-testid="mode-paiement-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                                            <SelectContent>
                                                {MODES_PAIEMENT.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer select-none -mt-1">
                                <input
                                    type="checkbox"
                                    checked={clientNonRegle}
                                    onChange={(e) => {
                                        setClientNonRegle(e.target.checked);
                                        if (e.target.checked) { setModePaiement(''); setPreuveFiles([]); }
                                    }}
                                    className="h-4 w-4 accent-[#2e7cd6]"
                                    data-testid="client-non-regle-checkbox"
                                />
                                <span className="text-sm text-slate-700 font-medium">Le client n'a pas réglé</span>
                            </label>

                            {!clientNonRegle && modePaiement !== 'espece' && (
                            <div>
                                <Label className="text-sm text-slate-700 mb-1 block">Preuve(s) de paiement (photos) *</Label>
                                {preuveFiles.length > 0 && (
                                    <div className="mb-2 space-y-1.5">
                                        {preuveFiles.map((f, idx) => (
                                            <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200">
                                                <Paperclip size={14} className="text-emerald-600 shrink-0" />
                                                <span className="flex-1 text-sm text-slate-700 truncate" title={f.name}>{f.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setPreuveFiles(prev => prev.filter((_, i) => i !== idx))}
                                                    className="p-1 rounded hover:bg-emerald-100 text-emerald-700"
                                                    data-testid={`remove-file-${idx}`}
                                                    aria-label="Retirer le fichier"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-2">
                                    <label className="flex items-center justify-center gap-2 p-3 rounded-md border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors">
                                        <Camera size={18} className="text-slate-500 shrink-0" />
                                        <span className="text-sm text-slate-600 truncate">Prendre une photo</span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            capture="environment"
                                            multiple
                                            className="hidden"
                                            onChange={async (e) => {
                                                const list = Array.from(e.target.files || []);
                                                e.target.value = '';
                                                if (!list.length) return;
                                                const compressed = await Promise.all(list.map(compressImage));
                                                setPreuveFiles(prev => [...prev, ...compressed]);
                                            }}
                                            data-testid="preuve-file-camera"
                                        />
                                    </label>
                                    <label className="flex items-center justify-center gap-2 p-3 rounded-md border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors">
                                        {preuveFiles.length > 0
                                            ? <Plus size={18} className="text-[#2e7cd6] shrink-0" />
                                            : <Upload size={18} className="text-slate-500 shrink-0" />}
                                        <span className="text-sm text-slate-600 truncate">Choisir depuis la galerie</span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            className="hidden"
                                            onChange={async (e) => {
                                                const list = Array.from(e.target.files || []);
                                                e.target.value = '';
                                                if (!list.length) return;
                                                const compressed = await Promise.all(list.map(compressImage));
                                                setPreuveFiles(prev => [...prev, ...compressed]);
                                            }}
                                            data-testid="preuve-file"
                                        />
                                    </label>
                                </div>
                                {preuveFiles.length > 0 && (
                                    <p className="text-xs text-slate-500 mt-1">{preuveFiles.length} fichier{preuveFiles.length > 1 ? 's' : ''} sélectionné{preuveFiles.length > 1 ? 's' : ''}</p>
                                )}
                            </div>
                            )}

                            <div>
                                <Label className="text-sm text-slate-700 mb-1 block">Signaler un problème</Label>
                                <Textarea rows={3} value={signalement} onChange={(e) => setSignalement(e.target.value)} placeholder="Décrire un problème rencontré (facultatif)..." data-testid="signalement" />
                            </div>

                            <Button type="submit" disabled={submitting} className="w-full bg-[#2e7cd6] hover:bg-[#2568b5] text-white" data-testid="submit-btn">
                                {submitting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Envoi en cours...</> : 'Envoyer'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
